import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Heart, HeartCrack } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Favorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavorites = async () => {
      const ids = JSON.parse(localStorage.getItem('favorites') || '[]');
      if (ids.length === 0) {
        setLoading(false);
        return;
      }
      try {
        const all = await axios.get(`${API}/exercises`);
        const filtered = all.data.filter(ex => ids.includes(ex.id));
        setFavorites(filtered);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-blue-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 space-y-3">
          <Heart className="h-12 w-12 mx-auto text-red-500 fill-current" />
          <h1 className="text-4xl md:text-6xl font-black text-gray-900">Your Favorites</h1>
          <p className="text-xl text-gray-600">Exercises you've saved for later</p>
        </div>

        {loading ? (
          <p className="text-center text-xl text-gray-600 py-20">Loading favorites...</p>
        ) : favorites.length === 0 ? (
          <Card className="border-2 border-gray-200 bg-white p-12 max-w-2xl mx-auto">
            <div className="text-center space-y-5">
              <HeartCrack className="h-16 w-16 mx-auto text-gray-400" />
              <h3 className="text-2xl font-bold text-gray-900">No favorites yet</h3>
              <p className="text-gray-600">Tap the heart icon on any exercise to save it here.</p>
              <Link to="/exercises">
                <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold">
                  Browse Exercises
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {favorites.map((exercise) => (
              <Card key={exercise.id} className="border-2 hover:border-orange-400 transition-all duration-200 hover:shadow-2xl transform hover:-translate-y-2 bg-white" data-testid={`fav-card-${exercise.id}`}>
                <CardHeader className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <Badge className="bg-orange-100 text-orange-700 font-semibold border border-orange-300">{exercise.skill}</Badge>
                    <Badge variant="outline" className={`font-semibold ${
                      exercise.difficulty === 'Beginner' ? 'border-green-500 text-green-700 bg-green-50' :
                      exercise.difficulty === 'Intermediate' ? 'border-blue-500 text-blue-700 bg-blue-50' :
                      'border-red-500 text-red-700 bg-red-50'
                    }`}>
                      {exercise.difficulty}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900">{exercise.title}</CardTitle>
                  <CardDescription className="line-clamp-2">{exercise.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to={`/exercise/${exercise.id}`}>
                    <Button className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold">
                      View Exercise
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Favorites;
